import React, { useState, useEffect } from 'react';
import { FormControl, Box, Button, Stack, Modal, Grid, TextField, Autocomplete, Select, MenuItem, InputLabel } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import api from '../network/ApiClient';
import { loadLocations } from '../locations/Locations';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    md: 500,
    xs: '100%',
  },
  bgcolor: 'background.paper',
  boxShadow: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
  p: {
    md: 4,
    xs: 2,
  },
};

const LocationSelector = ({ locations, selectedLocation, onLocationChange }) => (
  <Autocomplete
    options={locations.data}
    getOptionLabel={(loc) => loc.name}
    value={locations.data.find((loc) => loc.id === selectedLocation) || null}
    onChange={(event, newValue) => {
      onLocationChange(newValue?.id || '');
    }}
    renderInput={(params) => <TextField {...params} label="Select Starting Location" variant="outlined" fullWidth />}
    isOptionEqualToValue={(option, value) => option.id === value.id}
    freeSolo
  />
);

// `edit` — when set, this is the route being edited (from GET /route/{id}, so it
// carries name, organizergroup and cancellation). Editing reuses this modal but
// hides the starting-leg selector (legs are edited in the page body) and PUTs
// the changed fields instead of creating a new route.
export default function CreateRouteForm({ onCreateCallback, modalShow, setModalShow, onSubmitSuccess, edit }) {
  const [locations, setLocations] = useState({ data: [] });
  const [optionState, setOptionState] = useState({ data: [] });
  const isEditing = Boolean(edit);

  useEffect(() => {
    const fetchLocations = async () => {
      await loadLocations(setLocations);
    };
    fetchLocations();
    loadOrganizerGroups();
  }, []);

  const handleClose = () => {
    setModalShow(false);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      organizergroup: null,
      legs: [{ location: '', access: '2' }],
      cancellation: '', // New field for cancellation policy
      cancellationUnit: 'hours',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Route name is required'),
      organizergroup: Yup.object().required('Organizer Group is required.'),
      cancellation: Yup.string().required("Cancellation Policy is required."),
      // The starting leg is only collected on create; it's edited in the page body.
      ...(isEditing
        ? {}
        : {
            legs: Yup.array().of(
              Yup.object({
                location: Yup.string().required('Please select a location'),
                access: Yup.string().required('Access is required'),
              })
            ),
          }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const cancellationInHours = values.cancellationUnit === 'hours' ? values.cancellation : values.cancellation * 24;

        if (isEditing) {
          const res = await api.put(`${process.env.REACT_APP_API_URL}route/${edit.id}`, {
            name: values.name,
            organizergroup: values.organizergroup?.id,
            cancellation: cancellationInHours,
          });
          if (res.data?.error || !res.data?.updated) {
            toast.error(res.data?.error || 'Could not update route. Please try again.');
            return;
          }
          toast.success('Route updated');
          onSubmitSuccess?.();
          handleClose();
          return;
        }

        const legsWithIds = values.legs.map((leg, index) => ({
          id: `${values.name}-${index}`,
          ...leg,
        }));

        const newRoute = {
          id: `${values.name}-${Date.now()}`,
          organizergroup: values.organizergroup?.id,
          cancellation: cancellationInHours,
          name: values.name,
          legs: legsWithIds,
        };

        const res = await api.post(`${process.env.REACT_APP_API_URL}route`, newRoute);
        if (res.data?.error || !res.data?.added) {
          toast.error(res.data?.error || 'Could not create route. Please try again.');
          return;
        }
        toast.success('Route created');
        onCreateCallback?.(newRoute);
        onSubmitSuccess?.();
        handleClose();
      } catch (error) {
        console.error('Error submitting form:', error);
        toast.error(error?.response?.data?.error || 'Could not save route. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Prefill when editing. Re-runs once the merchant list loads so the route's
  // organizergroup id can resolve to the matching option object.
  useEffect(() => {
    if (!isEditing || !modalShow) return;
    formik.setValues({
      name: edit.name || '',
      organizergroup: optionState.data.find((o) => String(o.id) === String(edit.organizergroup)) || null,
      legs: [{ location: '', access: '2' }],
      cancellation: edit.cancellation != null ? String(edit.cancellation) : '',
      cancellationUnit: 'hours',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, modalShow, optionState.data]);

  const handleAddLegChange = (key, value) => {
    formik.setFieldValue('legs', [{ ...formik.values.legs[0], [key]: value }]);
  };

  const loadOrganizerGroups = async () => {
    await api
      .get('organizergroup', {
        params: {},
      })
      .then((res) => {
        const organizerGroups = res.data.organizer || [];
        setOptionState({ ...optionState, data: res.data.organizer });
              // If only one organizer group, set it directly in formik
        if (organizerGroups.length === 1 && !formik.values.organizergroup) {
          formik.setFieldValue('organizergroup', organizerGroups[0]);
        }
      })
      .catch((e) => {
        console.log('unable to  load operators ', e);
      });
  };

  return (
    <Modal
      open={modalShow}
      onClose={handleClose}
      aria-labelledby="create-route-modal"
      aria-describedby="create-route-form"
    >
      <Box sx={style}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <h4 className="card-title mb-0">{isEditing ? 'Edit Route' : 'Create Route'}</h4>

            <TextField
              fullWidth
              name="name"
              label="Route Name"
              onChange={formik.handleChange}
              value={formik.values.name}
              variant="outlined"
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              InputLabelProps={{ shrink: true }}
            />

          {optionState.data.length > 1 ? (
            <FormControl error={Boolean(formik.errors.organizergroup && formik.touched.organizergroup)} fullWidth>
              <Autocomplete
                options={optionState.data}
                onChange={(event, value) => {formik.setFieldValue('organizergroup', value);}}
                getOptionLabel={(option) => `${option.name}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Merchant"
                    variant="outlined"
                    error={Boolean(formik.errors.organizergroup && formik.touched.organizergroup)}
                    helperText={formik.errors.organizergroup && formik.touched.organizergroup ? formik.errors.organizergroup : ''}
                  />
                )}
                value={formik.values.organizergroup}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>
          ) : null}

            {!isEditing ? (
              <LocationSelector
                locations={locations}
                selectedLocation={formik.values.legs[0]?.location}
                onLocationChange={(value) => handleAddLegChange('location', value)}
              />
            ) : null}

<Grid container fullWidth>
            <Grid item xs={8}>
<TextField
              fullWidth
              name="cancellation"
              label="Cancellation Policy"
              type="number"
              onChange={formik.handleChange}
              value={formik.values.cancellation}
              variant="outlined"
              error={formik.touched.cancellation && Boolean(formik.errors.cancellation)}
              helperText={formik.touched.cancellation && formik.errors.cancellation}
              InputLabelProps={{ shrink: true }}
              sx={{ pr: 1 }}
            />
            </Grid>

            {/* Dropdown for selecting "hours" or "days" */}
            <Grid item xs={4}>
            <FormControl fullWidth sx={{ pl: 1 }}>
              <InputLabel>Cancellation Unit</InputLabel>
              <Select
                value={formik.values.cancellationUnit}
                onChange={(e) => formik.setFieldValue('cancellationUnit', e.target.value)}
                label="Cancellation Unit"
              >
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
              </Select>
            </FormControl>
            </Grid>
            </Grid>
          </Stack>

          <Grid container sx={{ mt: 2 }} justifyContent="space-between">
            <Grid item xs={6}>
              <Button
                variant="contained"
                type="submit"
                disabled={formik.isSubmitting} // Disable button while submitting
              >
                {isEditing ? 'Update' : 'Submit'}
              </Button>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Button color="error" onClick={handleClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
}
